import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { LineVariantSwapEvent } from '@theme/events';

/**
 * @typedef {Object} PackVariant
 * @property {number} id
 * @property {number} pack
 * @property {boolean} available
 */

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * When the line item belongs to a product with sibling "X pack" variants
 * (detected via data-pack-variants), the +/- buttons step through pack variants
 * instead of changing the quantity. The line quantity stays at 1.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Returns the parsed list of pack variants for the underlying product, or null
   * if this line is not a "pack stepper" line.
   * @returns {PackVariant[] | null}
   */
  getPackVariants() {
    const raw = this.dataset.packVariants;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) return null;
      return parsed
        .filter(
          (v) => v && typeof v.id === 'number' && typeof v.pack === 'number'
        )
        .sort((a, b) => a.pack - b.pack);
    } catch {
      return null;
    }
  }

  /**
   * Gets the current pack value (size of the currently selected pack variant).
   * @returns {number}
   */
  getCurrentPack() {
    return parseInt(this.dataset.currentPack || '0', 10) || 0;
  }

  /**
   * Finds the next available pack variant relative to the current pack.
   * @param {number} direction - 1 for next (bigger), -1 for previous (smaller)
   * @returns {PackVariant | null}
   */
  findAdjacentPackVariant(direction) {
    const variants = this.getPackVariants();
    if (!variants) return null;
    const currentPack = this.getCurrentPack();

    if (direction > 0) {
      return variants.find((v) => v.pack > currentPack && v.available) ?? null;
    }
    const smaller = variants.filter((v) => v.pack < currentPack && v.available);
    return smaller.length ? smaller[smaller.length - 1] ?? null : null;
  }

  /**
   * Handles the quantity increase event.
   * Swaps to next pack variant if pack stepping is enabled.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (this.getPackVariants()) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.#swapToAdjacentPack(1);
      return;
    }
    super.increaseQuantity(event);
  }

  /**
   * Handles the quantity decrease event.
   * Swaps to previous pack variant if pack stepping is enabled.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (this.getPackVariants()) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.#swapToAdjacentPack(-1);
      return;
    }
    super.decreaseQuantity(event);
  }

  /**
   * @param {1 | -1} direction
   */
  #swapToAdjacentPack(direction) {
    const target = this.findAdjacentPackVariant(direction);
    if (!target) return;

    const fromVariantId = this.dataset.variantId || '';
    const fromPack = this.getCurrentPack();
    const lineAttr = this.refs.quantityInput.dataset.cartLine;
    const line = Number(lineAttr) || 0;
    if (!line) return;

    this.refs.minusButton.disabled = true;
    this.refs.plusButton.disabled = true;

    this.dispatchEvent(
      new LineVariantSwapEvent({
        line,
        fromVariantId,
        toVariantId: String(target.id),
        fromPack,
        toPack: target.pack,
      })
    );
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled.
   * For pack-stepper lines, buttons reflect availability of next/prev pack.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;

    if (this.getPackVariants()) {
      minusButton.disabled = !this.findAdjacentPackVariant(-1);
      plusButton.disabled = !this.findAdjacentPackVariant(1);
      return;
    }

    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
